/**
 * 이상한 방 - 꾸미기 도구 (소품 고르기 → 자리 겨누기 → 놓기 · 내 것 걷기)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 3절(꾸미기 1분) · 4.1절(인당 배치 수 제한)
 *            `이상한방-상태모델-v0.1.md` 9.1절(하한 미달이면 프리셋 변주) · 11.3절(검증은 서버가 쥔다)
 *            로드맵 M10
 *
 * ## 낙관적으로 그리지 않는다 - 방이 **공유 상태**이기 때문이다
 *
 * 체인저 도구(`changerTool.ts`)는 초안을 먼저 그리고 호스트의 답으로 맞춘다. 그 방은 **내 것**이라 그래도 된다.
 * 꾸미기의 방은 전원의 것이다 - 내가 먼저 그리면 옆 사람이 같은 칸에 먼저 놓은 순간 내 화면만 거짓이 되고,
 * 그 거짓을 되돌리는 코드가 곧 "누가 이겼는가"의 두 번째 사본이 된다. 그래서 여기서는 **보내고 기다린다**:
 * 탭 → `place` → 호스트가 전원에게 `placed` → 그때 방에 나타난다. 한 왕복이 늘지만 화면과 방이 갈릴 수 없다.
 *
 * 대신 잃는 것을 두 가지로 메운다 - 고스트가 손가락을 따라다니고(`aimAt`), 남은 개수는 뷰가 즉시 말한다.
 *
 * ## 미리 잠그는 것은 하나뿐이다
 *
 * "소품을 골랐는가"만 이 파일이 안다. 경계·점유·인당 한도는 **묻지 않는다** - 집행자는 호스트의 `core/verbs`이고
 * (`matchDecorate.ts`) 여기서 같은 비교식을 들면 두 답이 갈리는 날이 온다. 고스트의 `valid`도 `canPlace`를 그대로
 * 부른 결과다 - 초록인데 놓으면 거부되는 미리보기는 도움이 아니라 거짓말이다.
 *
 * ## Three.js를 모른다
 *
 * 탭 → 대상은 `pick`으로, 고스트는 `onPreview`로 바깥(`app/stageDecorate.ts`)이 붙인다. `input/`은 `core`·`catalog`만
 * 본다 - 뷰·이벤트의 모양은 여기서 좁게 다시 적고 접착층(`game/matchSessionDecorate.ts`)이 구조적으로 맞춘다.
 */

import type { PaletteColor } from '../catalog/palette'
import { canPlace, type VerbRejection } from '../core/verbs'
import type { Catalog } from '../core/room'
import type {
  CatalogEntry,
  CatalogId,
  Cell3,
  InstanceId,
  ObjectInstance,
  RoomBounds,
  RoomState,
  Rot,
} from '../core/types'
import { ADD_ROT, pickChangerCatalog } from './changerLocks'
import type { ChangerCatalogView } from './changerToolTypes'
import type { PickMode, PickResult } from './tapPicker'

/* --- 모양 -------------------------------------------------------------------------- */

/** 뷰에서 도구가 읽는 것. `ClientView.self`의 좁은 사본이고 접착층이 옮겨 준다(`game/`을 보지 않기 위해). */
export type DecorateViewSnapshot = Readonly<{
  /** 지금 꾸밀 수 있는가. 호스트의 `self.canDecorate` 그대로다 - 거짓이면 어떤 탭도 받지 않는다. */
  active: boolean
  /** 더 놓을 수 있는 개수. 호스트의 `self.placementsLeft` 그대로다 - 도구가 다시 세지 않는다. */
  left: number
  /** 인당 한도. 띠가 점을 그 수만큼 찍는다. */
  limit: number
}>

/**
 * 띠(`ui/decorateBar.ts`)가 그리는 것 전부. 띠는 계산하지 않는다.
 *
 * 조각의 모양은 바꾸기 띠의 것을 **그대로 쓴다**(`ChangerCatalogView`) - 두 띠가 같은 스트립을 같은 손으로 그리는데
 * 타입만 둘이면 한쪽이 색을 잃는 날이 온다. 이름이 `Changer`인 것은 먼저 생겼기 때문이고, 뜻은 "카탈로그 조각 하나"다.
 */
export type DecorateBarView = Readonly<{
  left: number
  limit: number
  /** 고른 소품. 없으면 `null`이고 그때 탭은 걷기만 한다. */
  picked: CatalogId | null
  strip: readonly ChangerCatalogView[]
}>

/** 놓을 자리 미리보기. `changerToolTypes.ChangerPreview`와 같은 모양이라 무대가 같은 고스트를 쓴다. */
export type DecoratePreview = Readonly<{ cell: Cell3; catalogId: CatalogId; rot: Rot; valid: boolean }>

/** 탭 좌표 → 대상. `ChangerPick`과 같은 모양이라 무대가 같은 한 줄을 두 도구에 준다. */
export type DecoratePick = (clientX: number, clientY: number, room: RoomState, mode: PickMode) => PickResult | null

/** 도구가 보내는 것. `PlayerEvent`의 부분집합임을 접착층의 타입 대입이 컴파일에서 확인한다. */
export type DecorateToolEvent =
  | Readonly<{ type: 'place'; obj: Omit<ObjectInstance, 'placedBy'> }>
  | Readonly<{ type: 'unplace'; id: InstanceId }>

export type DecorateToolDeps = Readonly<{
  catalog: Catalog
  bounds: RoomBounds
  /** 조각의 색을 뽑는 표. 소품의 기본 색이 스트립의 색이다(바꾸기 띠와 같은 한 줄 - `changerToolView.ts`). */
  palette: readonly PaletteColor[]
  /** 스트립 추첨의 시드. 체인저와 같은 함수를 쓰므로 같은 판에서는 같은 여덟이다. */
  seed: string
  me: string
  /** 지금 방. 꾸미기의 방은 하나뿐이라 조회 함수다 - 호스트의 통지로 자라므로 값으로 들면 곧 낡는다. */
  room: () => RoomState | null
  view: () => DecorateViewSnapshot
  send: (event: DecorateToolEvent) => number
  pick: DecoratePick
  onView: (view: DecorateBarView) => void
  onPreview?: (preview: DecoratePreview | null) => void
  /** 놓을 수 없는 자리라 보내지도 않았다. 호스트 거부와 같은 말로 띄운다(`core/verbs`의 사유 그대로다). */
  onLocalReject: (reason: VerbRejection) => void
}>

export type DecorateTool = Readonly<{
  tapAt: (clientX: number, clientY: number) => void
  aimAt: (clientX: number, clientY: number) => void
  /** 자리를 고르는 중인가. 참이면 드래그가 화면을 옮기는 대신 겨눈다(`app/inputLayer.ts`). */
  isPlacing: () => boolean
  pickCatalog: (catalogId: CatalogId) => void
  /** 뷰가 갱신됐다(남은 개수가 바뀌었다). 띠를 다시 그린다. */
  onView: () => void
  getBarView: () => DecorateBarView
  dispose: () => void
}>

/* --- 본체 ------------------------------------------------------------------------ */

export function createDecorateTool(deps: DecorateToolDeps): DecorateTool {
  const strip = pickChangerCatalog(deps.catalog, deps.seed)
  /** 고른 소품. 처음에는 스트립의 첫 것이다 - 꾸미기의 기본 동작은 놓기이고, 빈 손으로 시작하면 첫 탭이 조용하다. */
  let picked: CatalogId | null = strip[0]?.id ?? null
  /** 내가 만든 id의 일련번호. 이름에 좌석이 들어가므로 남과 겹치지 않는다. */
  let addSeq = 0
  let disposed = false

  function roomOrNull(): RoomState | null {
    return deps.room()
  }

  function entryOf(id: CatalogId | null): CatalogEntry | undefined {
    return id === null ? undefined : deps.catalog.get(id)
  }

  function barView(): DecorateBarView {
    const view = deps.view()
    return {
      left: view.left,
      limit: view.limit,
      picked,
      strip: strip.map((entry) => ({
        id: entry.id,
        colorHex: deps.palette[entry.defaultColorId]?.hex ?? 0,
        selected: entry.id === picked,
      })),
    }
  }

  function refresh(): void {
    if (disposed) return
    deps.onView(barView())
  }

  function nextId(room: RoomState): InstanceId {
    let id: InstanceId
    do {
      addSeq += 1
      id = `${deps.me}-put-${String(addSeq)}`
    } while (room.objects.has(id))
    return id
  }

  /** 손가락이 가리키는 빈 자리. 물건을 맞혔으면 `null`이다(그 탭은 걷기의 것이다). */
  function spotAt(room: RoomState, clientX: number, clientY: number): Cell3 | null {
    const result = deps.pick(clientX, clientY, room, 'place')
    return result !== null && result.kind === 'place' ? result.cell : null
  }

  /** 손가락이 가리키는 **내가 놓은** 물건. 남의 것도 프리셋도 걷을 수 없다(`matchDecorate` 머리말). */
  function mineAt(room: RoomState, clientX: number, clientY: number): InstanceId | null {
    // `'search'`(무대가 반경 0으로 준다)라야 **몸통을 정확히 맞힌 탭**만 걷기가 된다. `'place'`는 옆면을 눌러도
    // 윗면 셀을 돌려주므로(3.2절 표면 스냅) 그것으로 판단하면 "위에 얹으려다 밑을 걷었다"가 생긴다.
    const result = deps.pick(clientX, clientY, room, 'search')
    if (result === null || result.kind !== 'object') return null
    const obj = room.objects.get(result.instanceId)
    // 방은 와이어를 지나 왔으므로 `placedBy`가 없다(2.1절) - 내 것인지는 **id의 접두사**로 안다.
    return obj !== undefined && result.instanceId.startsWith(`${deps.me}-put-`) ? result.instanceId : null
  }

  function isPlacing(): boolean {
    return deps.view().active && picked !== null && deps.view().left > 0
  }

  function aimAt(clientX: number, clientY: number): void {
    if (deps.onPreview === undefined) return
    const room = roomOrNull()
    const entry = entryOf(picked)
    if (room === null || entry === undefined || !isPlacing()) {
      deps.onPreview(null)
      return
    }
    const cell = spotAt(room, clientX, clientY)
    if (cell === null) {
      deps.onPreview(null)
      return
    }
    // `valid`는 집행자에게 그대로 물어본 답이다 - 여기서 경계·점유를 비교하면 그것이 규칙의 두 번째 사본이다.
    const probe = canPlace(room, deps.catalog, entry, cell, ADD_ROT, deps.bounds)
    deps.onPreview({ cell, catalogId: entry.id, rot: ADD_ROT, valid: probe.ok })
  }

  function tapAt(clientX: number, clientY: number): void {
    const room = roomOrNull()
    if (room === null || !deps.view().active) return

    // 내 물건을 누르면 걷는다. 놓기보다 먼저 보는 이유는 물건 위를 눌렀을 때의 뜻이 하나여야 하기 때문이다.
    const mine = mineAt(room, clientX, clientY)
    if (mine !== null) {
      deps.send({ type: 'unplace', id: mine })
      deps.onPreview?.(null)
      return
    }

    const entry = entryOf(picked)
    if (entry === undefined) return
    if (deps.view().left <= 0) return

    const cell = spotAt(room, clientX, clientY)
    if (cell === null) return

    /*
     * 보내기 전에 한 번 묻는다. 호스트가 어차피 다시 검사하므로 **규칙이 아니라 예의**다 -
     * 벽에 대고 탭할 때마다 왕복을 돌고 나서 거절 토스트를 보는 것보다, 그 자리에서 같은 말을 하는 편이 빠르다.
     * 답이 갈릴 수 없는 이유는 같은 함수를 부르기 때문이다(`core/verbs.canPlace`).
     */
    const probe = canPlace(room, deps.catalog, entry, cell, ADD_ROT, deps.bounds)
    if (!probe.ok) {
      deps.onLocalReject(probe.reason)
      return
    }

    deps.send({
      type: 'place',
      obj: { id: nextId(room), catalogId: entry.id, cell, rot: ADD_ROT, colorId: entry.defaultColorId },
    })
    deps.onPreview?.(null)
  }

  refresh()

  return {
    tapAt,
    aimAt,
    isPlacing,

    pickCatalog(catalogId: CatalogId): void {
      if (!strip.some((entry) => entry.id === catalogId)) return
      // 같은 것을 다시 누르면 손을 비운다 - 걷기만 하고 싶을 때 놓을 것을 들고 있으면 실수로 놓는다.
      picked = picked === catalogId ? null : catalogId
      deps.onPreview?.(null)
      refresh()
    },

    onView: refresh,
    getBarView: barView,

    dispose(): void {
      disposed = true
      deps.onPreview?.(null)
    },
  }
}
